import { Component, OnInit, OnDestroy } from "@angular/core";
import { SuperAdminService } from "src/app/super-admin.service";
import { ToastrService } from "ngx-toastr";
import { NgxSpinnerService } from "ngx-spinner";
import { debounce } from "lodash";
import { RoleService } from "src/app/core/services/role.service";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

@Component({
  selector: "app-own-vehicle-expence-list",
  templateUrl: "./own-vehicle-expence-list.component.html",
  styleUrls: ["./own-vehicle-expence-list.component.css"],
})
export class OwnVehicleExpenceListComponent implements OnInit, OnDestroy {
  ownVehicleExpenses: any[] = [];

  limit: number = 10;
  currentPage: number = 1;
  totalRecords: number = 0;
  userRole: string | null = null;
  searchText: string = "";
  fromDate: string = "";
  toDate: string = "";

  // debounced function reference
  debouncedSearch: any;

  constructor(
    private superAdminService: SuperAdminService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    public role: RoleService,
  ) {
    this.debouncedSearch = debounce((value: string) => {
      this.searchText = value;
      this.currentPage = 1;
      this.getOwnVehicleExpenses();
    }, 500);
  }

  ngOnInit(): void {
    this.getOwnVehicleExpenses();
  }

  ngOnDestroy(): void {
    this.debouncedSearch.cancel();
  }

  getOwnVehicleExpenses() {
    this.spinner.show();

    this.superAdminService
      .getOwnVehicleExpenseList(
        this.currentPage,
        this.limit,
        this.searchText,
        this.fromDate,
        this.toDate,
      )
      .subscribe(
        (res) => {
          this.spinner.hide();
          if (res.status) {
            this.ownVehicleExpenses = res.data || [];
            this.totalRecords = res.totalRecords || 0;
          } else {
            this.ownVehicleExpenses = [];
            this.totalRecords = 0;
          }
        },
        () => {
          this.spinner.hide();
          this.ownVehicleExpenses = [];
          this.totalRecords = 0;
        },
      );
  }

  /* ---------------- SEARCH ---------------- */
  onSearchChange(value: string) {
    this.debouncedSearch(value);
  }

  onDateFilterChange() {
    this.currentPage = 1;
    this.getOwnVehicleExpenses();
  }

  /* ---------------- PAGINATION ---------------- */
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.limit = event.pageSize;
    this.getOwnVehicleExpenses();
  }

  deleteOwnVehicleExpense(expenseId: string) {
    const confirmed = window.confirm(
      "Are you sure you want to delete this expense?",
    );

    if (!confirmed) return;

    this.spinner.show();

    this.superAdminService
      .deleteOwnVehicleExpense(expenseId)
      .subscribe((res) => {
        this.spinner.hide();
        if (res.status) {
          this.toastr.success("Expense deleted successfully", "Success");
          this.getOwnVehicleExpenses();
        } else {
          this.toastr.error("Failed to delete expense");
        }
      });
  }

  exportToExcel(): void {
    this.spinner.show();

    this.superAdminService
      .exportOwnVehicleExpense(this.searchText, this.fromDate, this.toDate)

      .subscribe({
        next: (res: any) => {
          this.spinner.hide();

          if (!res.status || !res.data || res.data.length === 0) {
            this.toastr.warning("No data to export!");
            return;
          }

          const exportData = res.data.map((item: any, i: number) => ({
            "S.No": i + 1,
            "Vehicle No": item?.vehicle?.vehicle_no || "",
            "Vehicle Type": item?.vehicle?.type || "",
            "Expense Date": item?.expenseDate
              ? new Date(item.expenseDate).toLocaleDateString()
              : "",
            Location: item?.location || "",
            "Start Meter": item?.startMeterReading || "",
            "End Meter": item?.endMeterReading || "",
            "Total KM": item?.totalKms || "",
            "Driver Advance": item?.driverAdvance || 0,
            "Loader Advance": item?.loaderAdvance || 0,
            "Police Amount": item?.policeAmount || 0,
            "Other Amount": item?.other || 0,
            "Other Description": item?.otherDescription || "",
            "Fuel Expense (CNG/DIESEL)": item?.fuelExpense || 0,
            "Total Amount": item?.totalAmount || 0,
            "Created By": item?.createdBy?.full_name || "",
            "Created At": item?.createdAt
              ? new Date(item.createdAt).toLocaleString()
              : "",
          }));

          const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

          ws["!cols"] = [
            { wch: 5 },
            { wch: 15 },
            { wch: 20 },
            { wch: 15 },
            { wch: 20 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 25 },
            { wch: 20 },
            { wch: 22 },
          ];

          const wb: XLSX.WorkBook = {
            Sheets: { "Own Vehicle Expenses": ws },
            SheetNames: ["Own Vehicle Expenses"],
          };

          const excelBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array",
          });

          saveAs(
            new Blob([excelBuffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
            `Own_Vehicle_Expenses_${new Date()
              .toISOString()
              .slice(0, 10)}.xlsx`,
          );

          this.toastr.success("Exported successfully");
        },
        error: () => {
          this.spinner.hide();
          this.toastr.error("Export failed!");
        },
      });
  }

  clearFilters() {
    this.searchText = "";
    this.fromDate = "";
    this.toDate = "";
    this.currentPage = 1;

    // clear search input UI
    const searchInput = document.querySelector(
      "input.search-box",
    ) as HTMLInputElement;

    if (searchInput) {
      searchInput.value = "";
    }

    this.getOwnVehicleExpenses();
    this.toastr.info("Filters cleared");
  }
}
